import type { PrivacySettings } from '@sirocco/shared';
import { users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { SettingGroup, SettingRow } from '../../components/ui/Bits';
import { Radio, Toggle } from '../../components/ui/Field';
import { useAsync } from '../../hooks/useAsync';
import { t } from '../../i18n';

/**
 * Ce que les autres membres voient de mon profil.
 *
 * Chaque reglage est applique a l'ecran avant l'aller-retour reseau :
 * on ne recharge pas la page entiere pour un interrupteur. Si l'API
 * refuse, `reload()` remet l'affichage en accord avec le serveur.
 */
export function ProfileVisibilityScreen() {
  const { data, loading, error, reload, setData } = useAsync(
    () => usersApi.settings(),
    [],
    'settings',
  );

  function patch(next: Partial<PrivacySettings>) {
    setData((previous) => ({ ...previous, privacy: { ...previous.privacy, ...next } }));
    usersApi.updatePrivacy(next).catch(reload);
  }

  const privacy = data?.privacy;

  return (
    <SubScreen title={t.profil.commun.visibilite}>
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && privacy && (
        <>
          <p className="pr-intro">
            {t.profil.visibilite.intro}
          </p>

          <SettingGroup title={t.profil.visibilite.quiPeutVoir}>
            <SettingRow
              label={t.profil.visibilite.toutLeServeur}
              onClick={() => patch({ profileVisibility: 'server' })}
              trailing={
                <Radio checked={privacy.profileVisibility === 'server'} label={t.profil.visibilite.toutLeServeur} />
              }
            />
            <SettingRow
              label={t.profil.visibilite.amisSeulement}
              onClick={() => patch({ profileVisibility: 'friends' })}
              trailing={
                <Radio
                  checked={privacy.profileVisibility === 'friends'}
                  label={t.profil.visibilite.amisSeulement}
                />
              }
            />
          </SettingGroup>

          <SettingGroup title={t.profil.visibilite.ceQuiApparait}>
            <SettingRow
              label={t.profil.commun.mesEvenements}
              description={t.profil.visibilite.evenementsAide}
              trailing={
                <Toggle
                  checked={privacy.showEvents}
                  onChange={(next) => patch({ showEvents: next })}
                  label={t.profil.commun.mesEvenements}
                />
              }
            />
            <SettingRow
              label={t.profil.commun.mesDestinations}
              description={t.profil.visibilite.mesDestinationsAide}
              trailing={
                <Toggle
                  checked={privacy.showTrophies}
                  onChange={(next) => patch({ showTrophies: next })}
                  label={t.profil.commun.mesDestinations}
                />
              }
            />
            <SettingRow
              label={t.profil.commun.mesPosts}
              description={t.profil.visibilite.postsAide}
              trailing={
                <Toggle
                  checked={privacy.showPosts}
                  onChange={(next) => patch({ showPosts: next })}
                  label={t.profil.commun.mesPosts}
                />
              }
            />
            <SettingRow
              label={t.profil.visibilite.enLigne}
              description={t.profil.visibilite.enLigneAide}
              trailing={
                <Toggle
                  checked={privacy.showOnlineStatus}
                  onChange={(next) => patch({ showOnlineStatus: next })}
                  label={t.profil.visibilite.enLigne}
                />
              }
            />
            {/*
              A part des autres : les trois du dessus se montrent ou se
              cachent, celui-ci choisit a qui. Il vit ici parce que c'est
              la meme question — ce qu'on laisse voir de soi —, et sa
              description dit ce que l'interrupteur ne peut pas dire.
            */}
            <SettingRow
              label={t.profil.visibilite.destinationsAmis}
              description={t.profil.visibilite.destinationsAide}
              trailing={
                <Toggle
                  checked={privacy.destinationsFriendsOnly}
                  onChange={(next) => patch({ destinationsFriendsOnly: next })}
                  label={t.profil.visibilite.destinationsAmis}
                />
              }
            />
          </SettingGroup>

          <SettingGroup title={t.profil.visibilite.messages}>
            <SettingRow
              label={t.profil.commun.membresBloques}
              value={String(data.blockedCount)}
              to="/app/profile/settings/blocked"
            />
          </SettingGroup>
        </>
      )}
    </SubScreen>
  );
}
